import { motion } from "framer-motion";
import { PageHero, SectionHeading, reveal } from "@/components/site/primitives";
import Seo from "@/components/site/Seo";
import { CTABand } from "@/pages/BusinessWaste";
import { IMAGES } from "@/lib/data";
import { useContent } from "@/i18n/content";
import { useI18n } from "@/i18n";

export default function WhoWeAre() {
  const { t } = useI18n();
  const { ABOUT_STORY, PILLARS, STATS } = useContent();
  const aboutJsonLd = {
    "@context": "https://schema.org",
    "@type": "AboutPage",
    name: t("wwa.seoTitle"),
    description: t("wwa.seoDesc"),
    url: "https://eewaste.com/who-we-are",
    mainEntity: { "@id": "https://eewaste.com/#business" },
  };
  return (
    <>
      <Seo
        title={t("wwa.seoTitle")}
        description={t("wwa.seoDesc")}
        path="/who-we-are"
        image={IMAGES.recycling}
        jsonLd={aboutJsonLd}
      />
      <PageHero
        crumb={t("wwa.crumb")}
        eyebrow={t("wwa.eyebrow")}
        title={t("wwa.title")}
        highlight={t("wwa.highlight")}
        subtitle={t("wwa.subtitle")}
      />

      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <SectionHeading eyebrow={t("wwa.storyEyebrow")} title={t("wwa.storyTitle")} highlight={t("wwa.storyHighlight")} />
            <p className="mt-6 text-slate-600 leading-relaxed">{ABOUT_STORY}</p>
          </motion.div>
          <motion.div variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
            className="relative rounded-3xl overflow-hidden aspect-[4/3] border border-[#E7E5DF] shadow-soft">
            <img loading="lazy" decoding="async" src={IMAGES.recycling} alt="EWS recycling operations" className="w-full h-full object-cover" />
          </motion.div>
        </div>
      </section>

      <section className="relative bg-[#0E3B3B] py-16">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
          {STATS.map((s, i) => (
            <div key={s.label}>
              <div className="font-display font-extrabold text-4xl sm:text-5xl text-white">{s.metric}</div>
              <div className="mt-2 text-white/70 text-sm">{t(`stat.${i}`)}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="relative bg-[#FBFAF8] py-24">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <SectionHeading center eyebrow={t("wwa.whyEyebrow")} title={t("wwa.whyTitle")} highlight={t("wwa.whyHighlight")} className="mb-14" />
          <div className="grid md:grid-cols-2 gap-px bg-[#E7E5DF] border border-[#E7E5DF] rounded-3xl overflow-hidden">
            {PILLARS.map((p, i) => (
              <motion.div key={p.num} custom={i} variants={reveal} initial="hidden" whileInView="visible" viewport={{ once: true }}
                className="bg-white p-8 sm:p-12 hover:bg-[#FBFAF8] transition-colors group">
                <div className="flex items-start gap-6">
                  <span className="font-display font-extrabold text-5xl sm:text-6xl text-[#F0EFEA] group-hover:text-emerald-600/40 transition-colors leading-none">{p.num}</span>
                  <div>
                    <h3 className="font-display font-bold text-xl sm:text-2xl text-[#0B0F14] mb-3">{p.title}</h3>
                    <p className="text-slate-600 text-sm sm:text-base leading-relaxed">{p.desc}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <CTABand />
    </>
  );
}
